/**
 * ACH form calls for the public link flow (/link/ach): vendor-service's @PublicLinkEndpoint endpoints, authorized by the
 * public link session (Authorization: Bearer). Never sends a loan id or token: the server takes the loan and borrower
 * from the session.
 *
 * The bank step uses the same method names as PrivatePlaidService, so AchFormBaseStore and the ACH screens work unchanged.
 */
const BASE = '/api/v1/vendor/public/link/ach';

export type PublicLinkRequest = <T>(path: string, init?: RequestInit) => Promise<T>;

const digits = (value: string) => (value ?? '').replace(/\D/g, '');

/** What the page shows (vendor-service AchLinkDataResponse): this borrower only, no bank details. */
export interface AchLinkData {
  loanId: number;
  borrowerFirstName?: string;
  borrowerNames?: string[];
  lenderName?: string;
  propertyAddresses?: string[];
  verificationStep?: string;
  withdrawalDay?: number;
}

/** The bank information form's values (BankInformationFormStore). */
export interface BankInformationValues {
  bankName: string;
  accountName: string;
  accountType: string;
  accountNumber: string;
  routingNumber: string;
  withdrawalDay: number;
}

export class PublicLinkAchService {
  constructor(private readonly request: PublicLinkRequest) {}

  /** Shaped like an axios response ({ data }), which is what AchFormBaseStore.fetchACHData reads. */
  getACHData() {
    return this.request<AchLinkData>(`${BASE}/data`).then(data => ({ data }));
  }

  /** The Bank of America check now runs server-side as part of saving the bank information. */
  sentBofaValidation(..._ignored: unknown[]) {
    return Promise.resolve();
  }

  saveBankInformation(values: BankInformationValues) {
    return this.request(`${BASE}/bank-information`, {
      method: 'POST',
      body: JSON.stringify({
        bankName: values.bankName,
        accountName: values.accountName,
        accountType: values.accountType,
        accountNumber: digits(values.accountNumber),
        routingNumber: digits(values.routingNumber),
        withdrawalDay: values.withdrawalDay,
      }),
    });
  }

  generateAchDocument(data: { signature: string; userAgent: string }) {
    return this.request(`${BASE}/sign`, {
      method: 'POST',
      body: JSON.stringify({ signature: data.signature, userAgent: data.userAgent }),
    });
  }
}
